import { useMemo } from 'react';

import { useMatches } from '../api/hooks';
import type { Match } from '../api/types';
import MatchCard from '../components/MatchCard';

/** Local calendar-day key (yyyy-mm-dd) for a timestamp. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format a day key (yyyy-mm-dd) as the Razor's "yyyy. MM. dd." heading. */
function formatDayHeading(key: string): string {
  const [y, m, d] = key.split('-');
  return `${y}. ${m}. ${d}.`;
}

const chevron = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6 ml-2 transition-transform duration-200 group-open:rotate-90"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

/**
 * NapokPage — like MeccsekPage, but shows every day. Matches are grouped by
 * calendar day and sorted chronologically; each day is a <details> section of
 * the same MatchCard grid. URL-only (not linked in the navbar).
 */
export default function NapokPage() {
  const { data: matches, isLoading } = useMatches();

  const now = useMemo(() => new Date().toISOString(), []);

  const days = useMemo(() => {
    const all = matches ?? [];

    const byStart = (a: Match, b: Match) =>
      new Date(a.start).getTime() - new Date(b.start).getTime();

    const byDay = new Map<string, Match[]>();
    for (const m of all) {
      const k = dayKey(m.start);
      const list = byDay.get(k);
      if (list) list.push(m);
      else byDay.set(k, [m]);
    }

    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, list]) => ({ key, list: [...list].sort(byStart) }));
  }, [matches]);

  if (isLoading) {
    return (
      <div className="p-4 md:px-8">
        <p className="text-center">Betöltés...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:px-8">
      {days.length === 0 && (
        <p className="text-center">Itt fognak megjelenni a meccsek.</p>
      )}

      {days.map((entry, i) => (
        <details key={entry.key} open className={i === 0 ? 'group' : 'mt-6 group'}>
          <summary className="text-2xl font-bold mb-4 text-clay cursor-pointer select-none flex items-center">
            <span className="text-ink mr-1.5">{formatDayHeading(entry.key)}</span> Meccsek
            {chevron}
          </summary>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {entry.list.map((m) => (
              <MatchCard key={m.id} match={m} now={now} />
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}
